import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useUserMutations } from './useSettings';

/**
 * Add-user modal (SCRUM-461): full name + email (required) + phone, creating a
 * Pending user via POST /api/settings/users. Ports the prototype's Add User
 * modal. A newly added user is Pending until an admin assigns a role in the
 * users table (role assignment lives in the table's Assign-role select).
 *
 * Built on the shared Dialog primitive (radix — backdrop/Escape/focus-trap/a11y
 * handled). The width override targets the `sm:` variant too, because
 * DialogContent ships `sm:max-w-sm` (SCRUM-209 gotcha) — a plain max-w would not
 * win the cascade.
 */
export function AddUserModal({ onClose }: { onClose: () => void }) {
  const { add, isAdding } = useUserMutations();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && email.trim().length > 0 && !isAdding;

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      await add({
        displayName: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add user.');
    }
  };

  const inputCls =
    'bg-bg-3 border-brand-primary rounded-tk-sm text-text-0 border px-2.5 py-1.5 font-body text-[0.8125rem] font-medium outline-none';
  const labelCls =
    'text-text-3 flex flex-col gap-1.5 font-mono text-[0.625rem] font-semibold uppercase tracking-[0.06em]';

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="w-[420px] max-w-[min(420px,calc(100vw-2rem))] gap-0 p-0 sm:max-w-[420px]">
        <DialogHeader className="border-border-0 border-b px-5 py-4">
          <DialogTitle className="text-text-0 font-display text-[1rem] font-semibold">
            Add user
          </DialogTitle>
          <DialogDescription className="sr-only">
            Add a new user to your bank. They start pending until you assign a role.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 px-5 py-4">
          <label className={labelCls}>
            Full name
            <input
              aria-label="Full name"
              value={name}
              placeholder="Jane Doe"
              onChange={(e) => {
                setName(e.target.value);
              }}
              className={inputCls}
            />
          </label>
          <label className={labelCls}>
            Email
            <input
              aria-label="Email"
              type="email"
              value={email}
              placeholder="jane@meridiancommunity.example"
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              className={inputCls}
            />
          </label>
          <label className={labelCls}>
            Phone
            <input
              aria-label="Phone"
              type="tel"
              value={phone}
              placeholder="(540) 555-0000"
              onChange={(e) => {
                setPhone(e.target.value);
              }}
              className={inputCls}
            />
          </label>
          {error && <p className="text-[0.75rem] text-[#c84a4a]">{error}</p>}
        </div>

        <DialogFooter className="border-border-0 flex justify-end gap-2 border-t px-5 pt-3 pb-4">
          <button
            type="button"
            onClick={onClose}
            className="bg-bg-3 border-border-1 rounded-tk-sm text-text-1 hover:bg-bg-2 hover:text-text-0 font-body border px-3.5 py-1.5 text-[0.75rem]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void submit()}
            className="rounded-tk-sm inline-flex items-center px-3.5 py-1.5 text-[0.75rem] font-semibold text-white [background:var(--grad-cyan)] disabled:opacity-50"
          >
            {isAdding ? 'Adding…' : 'Add user'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
